import { useCallback, useRef } from 'react';

/**
 * A hook that ensures only one instance of an asynchronous operation is running at a time.
 * If the operation is called again while it's still running, it will return the same promise
 * instead of starting a new one.
 *
 * @template Result The type of the result returned by the asynchronous operation.
 * @returns A function that takes an asynchronous operation and returns a promise of its result.
 */
export const useSingleFlight = <Result>() => {
  const activePromiseRef = useRef<Promise<Result> | null>(null);

  return useCallback((operation: () => Promise<Result>) => {
    if (activePromiseRef.current) {
      return activePromiseRef.current;
    }

    const activePromise = operation().finally(() => {
      if (activePromiseRef.current === activePromise) {
        activePromiseRef.current = null;
      }
    });
    activePromiseRef.current = activePromise;
    return activePromise;
  }, []);
};
